<template>
  <div class="facebook-page-widget" :class="widgetClasses" :style="widgetStyles">
    <h3 v-if="widget.title" class="facebook-page-title">{{ widget.title }}</h3>

    <div v-if="!pageUrl" class="facebook-page-placeholder">
      <i class="bi bi-facebook"></i>
      <p>Ορίστε URL σελίδας Facebook στο widget editor</p>
    </div>

    <div v-else ref="embedRoot" class="facebook-page-embed">
      <div
        v-if="measuredWidth > 0"
        :key="embedKey"
        class="fb-page"
        :data-href="pageUrl"
        :data-tabs="widget.fbTabs || 'timeline'"
        :data-width="measuredWidth"
        data-small-header="false"
        data-adapt-container-width="true"
        data-hide-cover="false"
        data-show-facepile="true"
      >
        <blockquote :cite="pageUrl" class="fb-xfbml-parse-ignore">
          <a :href="pageUrl" target="_blank" rel="noopener noreferrer">
            {{ widget.pageName || 'Facebook Page' }}
          </a>
        </blockquote>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import type { FacebookPageWidget as FacebookPageWidgetType } from '@/types/pagebuilder'
import { useWidgetStyles } from '@/composables/useWidgetStyles'
import { normalizeFacebookPageUrl, observeFacebookEmbed } from '@/utils/facebookSdk'

interface Props {
  widget: FacebookPageWidgetType
}

const props = defineProps<Props>()
const embedRoot = ref<HTMLElement | null>(null)
const measuredWidth = ref(0)
const embedKey = ref(0)
const { widgetStyles, widgetClasses } = useWidgetStyles(props.widget)

let resizeObserver: ResizeObserver | null = null
let resizeTimer: ReturnType<typeof setTimeout> | null = null
let stopFbObserve: (() => void) | null = null

const FB_MIN_WIDTH = 180
const FB_MAX_WIDTH = 500

const pageUrl = computed(() => normalizeFacebookPageUrl(props.widget.pageUrl || ''))

const clampWidth = (width: number) =>
  Math.max(FB_MIN_WIDTH, Math.min(FB_MAX_WIDTH, Math.round(width)))

const measureContainer = () => {
  if (!embedRoot.value) return

  const width = clampWidth(
    embedRoot.value.clientWidth || embedRoot.value.getBoundingClientRect().width
  )

  if (width > 0 && width !== measuredWidth.value) {
    measuredWidth.value = width
    embedKey.value += 1
  }
}

const scheduleMeasure = () => {
  if (resizeTimer) clearTimeout(resizeTimer)
  resizeTimer = setTimeout(measureContainer, 120)
}

const renderEmbed = async () => {
  if (!pageUrl.value || !embedRoot.value || measuredWidth.value <= 0) return
  await nextTick()
  stopFbObserve?.()
  stopFbObserve = observeFacebookEmbed(embedRoot.value)
}

watch(
  () => [embedKey.value, props.widget.pageUrl, props.widget.fbTabs],
  () => {
    renderEmbed()
  }
)

onMounted(async () => {
  await nextTick()
  requestAnimationFrame(() => {
    measureContainer()
    renderEmbed()
  })

  if (embedRoot.value && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(scheduleMeasure)
    resizeObserver.observe(embedRoot.value)
  } else {
    window.addEventListener('resize', scheduleMeasure)
  }
})

onUnmounted(() => {
  stopFbObserve?.()
  resizeObserver?.disconnect()
  if (resizeTimer) clearTimeout(resizeTimer)
  window.removeEventListener('resize', scheduleMeasure)
})
</script>

<style scoped>
.facebook-page-widget {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
}

.facebook-page-title {
  margin: 0 0 1rem;
  font-size: 1.1rem;
  font-weight: 600;
}

.facebook-page-embed {
  width: 100%;
  max-width: 100%;
  min-height: 120px;
  overflow: hidden;
}

.facebook-page-embed :deep(.fb-page),
.facebook-page-embed :deep(.fb-page > span),
.facebook-page-embed :deep(iframe) {
  width: 100% !important;
  max-width: 100% !important;
}

.facebook-page-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem 1rem;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  color: #64748b;
  background: #f8fafc;
  text-align: center;
}

.facebook-page-placeholder i {
  font-size: 2rem;
  color: #1877f2;
}

.facebook-page-placeholder p {
  margin: 0;
  font-size: 0.9rem;
}
</style>
